import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, IndianRupee, Star, ArrowRight, ShieldCheck, MessageCircle } from "lucide-react";
import Nav from "@/components/site/Nav";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { payWithRazorpay } from "@/lib/razorpay";
import { HERO_SEQUENCE, PHASES, PROGRAMME_DELIVERABLES } from "@/data/diet";

const ORDER = ["jaytrial", "jay15", "jay30", "jay90", "jay180", "jay365", "jay365pro"];

function HeroSequence() {
  const [i, setI] = useState(0);
  const done = i >= HERO_SEQUENCE.length;
  useEffect(() => {
    if (done) return;
    const t = setTimeout(() => setI((v) => v + 1), i < 3 ? 1600 : 700);
    return () => clearTimeout(t);
  }, [i, done]);
  return (
    <div className="h-40 md:h-48 flex items-center justify-center" data-testid="diet-hero-sequence">
      <AnimatePresence mode="wait">
        {!done ? (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.45 }}
            className={`text-center font-serif-display tracking-tight ${i < 3 ? "text-3xl md:text-5xl text-[#F7F5F0]" : "text-4xl md:text-6xl text-[#E8C9B8] tracking-[0.15em]"}`}
          >
            {HERO_SEQUENCE[i]}
          </motion.div>
        ) : (
          <motion.div key="final" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="text-center">
            <div className="font-serif-display text-5xl md:text-7xl text-[#F7F5F0] tracking-tighter">JAY DIETS™</div>
            <div className="mt-3 text-sm md:text-base uppercase tracking-[0.35em] text-[#C9CFC0]">Adaptive Personalised Nutrition</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DietLanding() {
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "India" });
  const [paying, setPaying] = useState(false);
  const [cuisines, setCuisines] = useState([]);
  const { formatInr, format, currency, isInr } = useCurrency();
  const navigate = useNavigate();
  // Region always follows the live geo-detected currency (India → ₹ plans as-is; others → US$ converted)
  const region = isInr ? "india" : "international";

  useEffect(() => {
    axios.get(`${API_BASE}/diet/programmes`).then((r) => setData(r.data)).catch(() => {});
    axios.get(`${API_BASE}/diet/global-series`).then((r) => setCuisines(r.data)).catch(() => {});
  }, []);

  const progs = data?.programmes || {};
  const priceLabel = (p) => {
    if (p.trial) return "FREE";
    if (p.enquire) return "Enquire";
    return region === "india" ? `₹${Number(p.inr).toLocaleString("en-IN")}` : format(p.usd);
  };

  const startTrial = async () => {
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Please enter your name and a valid email");
      return;
    }
    if (!form.country.trim()) { toast.error("Please enter your country"); return; }
    const phone = form.phone.replace(/[\s\-().]/g, "");
    if (!/^\+\d{8,15}$/.test(phone)) {
      toast.error("Enter your phone with country code, e.g. +919876543210");
      return;
    }
    setPaying(true);
    try {
      await axios.post(`${API_BASE}/diet/trial`, {
        name: form.name.trim(), email: form.email.trim(), phone, country: form.country.trim(),
      });
      toast.success("Your free TRIAL JAY 7 is activated! Check your email, then create your account.");
      navigate(`/login?trial=1&mode=signup&redirect=${encodeURIComponent("/diet/onboarding")}&email=${encodeURIComponent(form.email.trim())}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start your trial");
    } finally {
      setPaying(false);
    }
  };

  const startPay = async () => {
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Please enter your name and a valid email");
      return;
    }
    setPaying(true);
    try {
      await payWithRazorpay({
        orderPayload: {
          order_type: "diet", programme_id: buying, pricing_region: region,
          name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || null,
        },
        onPaid: () => {
          toast.success("Welcome to JAY DIETS™! Let's begin your assessment.");
          navigate("/diet/onboarding");
        },
        onError: (msg) => toast.error(msg || "Payment could not be completed"),
        onDismiss: () => setPaying(false),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      {/* Hero */}
      <section className="bg-[#1A211A] pt-32 pb-20 md:pt-40 md:pb-28 px-6">
        <HeroSequence />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }} className="max-w-2xl mx-auto text-center mt-8">
          <h1 className="font-serif-display text-2xl md:text-3xl text-[#F7F5F0] tracking-tight">Your diet should adapt to you.</h1>
          <p className="mt-4 text-[#C9CFC0] leading-relaxed text-sm md:text-base">
            Personalised nutrition plans that evolve as you progress — powered by the JAY 7™ methodology
            and supported by human coaching when you need it. No app to download. No dashboard to learn.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#programmes" data-testid="diet-cta-start" className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#C57B57] text-white font-medium hover:bg-[#AD6A48] transition-colors">
              Start Your JAY Journey <ArrowRight size={15} />
            </a>
            <a href="/diet/how-it-works" data-testid="diet-cta-how" className="inline-flex items-center rounded-full px-8 py-3.5 border border-[#F7F5F0]/30 text-[#F7F5F0] font-medium hover:bg-white/10 transition-colors">
              How JAY Works
            </a>
          </div>
        </motion.div>
      </section>

      {/* Positioning */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["Not another diet app", "The deliverable is a professionally designed JAY 7™ Personal Nutrition Plan — practical, editorial, yours."],
            ["Human where it matters", "Plans are reviewed and adapted by a coach — via WhatsApp, email or a call — not left to an algorithm."],
            ["Independence is the goal", "Don't stay on a diet forever. JAY teaches you to own your nutrition for life."],
          ].map(([t, d], i) => (
            <motion.div key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }} className="bg-white border border-[#E5E2D9] rounded-2xl p-6">
              <div className="font-serif-display text-xl mb-2">{t}</div>
              <p className="text-sm text-[#5C665A] leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Phase timeline */}
      <section className="bg-[#2D372B] py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="overline !text-[#A9B39D] mb-2">The JAY Phase System</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#F7F5F0] tracking-tight mb-10">Twelve phases. One journey to independence.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="diet-phase-grid">
            {PHASES.map((p, i) => (
              <motion.div key={p.n} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.08 }} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A9B39D]">Phase {p.n} · {p.days}</div>
                <div className="font-serif-display text-xl text-[#E8C9B8] mt-1">{p.name}</div>
                <p className="text-xs text-[#C9CFC0] mt-1.5">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programmes */}
      <section id="programmes" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="overline mb-2">JAY Programmes</div>
            <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight">Choose your journey.</h2>
          </div>
          <div className="text-xs text-[#5C665A] bg-white border border-[#E5E2D9] rounded-full px-4 py-2" data-testid="diet-currency-note">
            Personalised pricing — chat with Coach Jay on WhatsApp
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ORDER.filter((id) => progs[id]?.available !== false).map((id) => {
            const p = progs[id] || {};
            const dels = PROGRAMME_DELIVERABLES[id] || [];
            return (
              <div key={id} data-testid={`programme-card-${id}`} className={`relative bg-white rounded-3xl border p-7 flex flex-col ${p.popular ? "border-[#C57B57] ring-2 ring-[#C57B57]/25" : "border-[#E5E2D9]"}`}>
                {p.popular && <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-[#C57B57] text-white text-[11px] font-semibold px-3 py-1"><Star size={11} className="fill-white" /> MOST POPULAR</span>}
                {p.best_value && <span className="absolute -top-3 left-6 rounded-full bg-[#2D372B] text-[#E8C9B8] text-[11px] font-semibold px-3 py-1">BEST VALUE</span>}
                {p.trial && <span className="absolute -top-3 left-6 rounded-full bg-[#89937C] text-white text-[11px] font-semibold px-3 py-1">FREE TRIAL · ONCE PER PERSON</span>}
                <div className="font-serif-display text-2xl">{p.name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#AD6A48] mt-1">{p.tagline}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  {p.trial ? (
                    <>
                      <span className="font-serif-display text-4xl" data-testid={`price-${id}`}>FREE</span>
                      <span className="text-xs text-[#5C665A]">for {p.days} days</span>
                    </>
                  ) : p.enquire ? (
                    <span className="font-serif-display text-4xl" data-testid={`price-${id}`}>Enquire</span>
                  ) : (
                    <span className="font-serif-display text-4xl" data-testid={`price-${id}`}>{p.days}<span className="text-lg text-[#5C665A] ml-1.5">days</span></span>
                  )}
                </div>
                <div className="text-xs text-[#5C665A] mt-1">{p.coverage}</div>
                <ul className="mt-5 space-y-2 flex-1">
                  {dels.map((d) => (
                    <li key={d} className="flex items-start gap-2 text-sm text-[#5C665A]">
                      <Check size={14} className="text-[#89937C] mt-0.5 shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
                {p.trial ? (
                  <button type="button" data-testid={`buy-${id}`} onClick={() => setBuying(id)}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-medium transition-colors bg-[#2D372B] text-white hover:bg-[#89937C]">
                    Start {p.name}
                  </button>
                ) : p.enquire ? (
                  <a href={`https://wa.me/919311390365?text=${encodeURIComponent(`Hi Coach, I'm interested in ${p.name}`)}`} target="_blank" rel="noreferrer" data-testid={`enquire-${id}`}
                    className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-3 border border-[#2D372B] font-medium hover:bg-[#2D372B] hover:text-white transition-colors">
                    Enquire on WhatsApp
                  </a>
                ) : (
                  <a href={`https://wa.me/919311390365?text=${encodeURIComponent(`Hi Coach Jay! I'd like to start ${p.name} (${p.days} days). Please share the pricing and next steps.`)}`}
                    target="_blank" rel="noreferrer" data-testid={`buy-${id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-medium transition-colors ${p.popular ? "bg-[#C57B57] text-white hover:bg-[#AD6A48]" : "bg-[#2D372B] text-white hover:bg-[#89937C]"}`}>
                    <MessageCircle size={15} /> Start {p.name} — WhatsApp
                  </a>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-8 text-xs text-[#5C665A] flex items-start gap-2 max-w-3xl">
          <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#89937C]" />
          JAY DIETS provides general personalised nutrition coaching. It is not a substitute for medical diagnosis or treatment.
          Please consult a qualified medical professional for any health condition.
        </p>
      </section>

      {/* JAY 7 Global Series teaser */}
      {cuisines.length > 0 && (
        <section className="bg-[#1A211A] py-16 md:py-20 px-6" data-testid="global-series-teaser">
          <div className="max-w-7xl mx-auto">
            <div className="text-xs font-bold tracking-[0.2em] text-[#C57B57] uppercase">JAY 7™ Global Series</div>
            <h2 className="mt-2 font-serif-display text-3xl md:text-4xl text-[#F7F5F0]">Seven cuisines. Seven days.<br className="hidden md:block" /> One fat-loss engine.</h2>
            <p className="mt-2 text-sm text-[#9AA294] max-w-xl">Members unlock calorie-tracked 7-day fat-loss weeks in Indian, American, African, Middle Eastern, Mediterranean, Latin and Japanese cuisine — with a downloadable grocery-list PDF.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {cuisines.map((c) => (
                <a key={c.id} href={`/diet/global-series?c=${c.id}`} data-testid={`teaser-${c.id}`}
                  className="group rounded-2xl overflow-hidden border border-white/10 hover:border-[#C57B57] transition-colors">
                  <div className="relative h-24 sm:h-28 overflow-hidden">
                    <img src={c.image} alt={c.short} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                    <div className="absolute bottom-2 left-2.5 right-2">
                      <div className="text-white font-serif-display text-sm leading-tight">{c.short}</div>
                      <div className="text-white/70 text-[9px]">~{c.avg_kcal} kcal/day</div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
            <a href="/diet/global-series" data-testid="teaser-explore" className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] transition-colors">
              Explore the Global Series →
            </a>
          </div>
        </section>
      )}

      {/* Buy dialog */}
      <Dialog open={!!buying} onOpenChange={(o) => !o && setBuying(null)}>
        <DialogContent className="bg-[#F7F5F0] border-[#E5E2D9] max-w-md" data-testid="diet-buy-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {progs[buying]?.name} — {buying && priceLabel(progs[buying] || {})}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="diet-buy-name" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white h-12 rounded-xl" />
            <Input data-testid="diet-buy-email" type="email" placeholder="Email * (you'll log in with this)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white h-12 rounded-xl" />
            {progs[buying]?.trial ? (
              <>
                <Input data-testid="diet-trial-country" placeholder="Country *" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="bg-white h-12 rounded-xl" />
                <Input data-testid="diet-buy-phone" placeholder="Phone with country code * (e.g. +919876543210)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white h-12 rounded-xl" />
                <button type="button" data-testid="diet-trial-start" onClick={startTrial} disabled={paying}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#89937C] text-white font-medium hover:bg-[#5C665A] transition-colors disabled:opacity-60">
                  {paying ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                  Start My Free 7-Day Trial
                </button>
                <p className="text-[11px] text-[#5C665A] text-center">Free — no card needed. One trial per person. After activation you'll complete your nutrition assessment.</p>
              </>
            ) : (
              <>
                <Input data-testid="diet-buy-phone" placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white h-12 rounded-xl" />
                <button type="button" data-testid="diet-buy-pay" onClick={startPay} disabled={paying}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-white font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
                  {paying ? <Loader2 size={15} className="animate-spin" /> : <IndianRupee size={15} />}
                  Pay {buying && priceLabel(progs[buying] || {})} — Secure via Razorpay
                </button>
                {region === "international" && <p className="text-[11px] text-[#5C665A] text-center">Charged in INR equivalent of the USD price at today's rate.</p>}
                <p className="text-[11px] text-[#5C665A] text-center">After payment you'll complete your nutrition assessment.</p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
